import type { ComposerInitialValues, ComposerLayout, FolderTreeItem, DraggedMailItem } from '@platform/ui';
import { createContext, useContext, type ReactNode, type Dispatch, type SetStateAction, type RefObject } from 'react';
import type { Folder } from './api';
import type { SearchRequest } from './api';
import type { DraftAttachment } from './compose-attachments';

/** What the composer does on send (DESIGN.md T11). */
export type ComposeMode = 'new' | 'reply' | 'replyAll' | 'forward' | 'forwardAsAttachment' | 'draft';

export interface ComposeRequest {
  mode: ComposeMode;
  draft?: { id: string; version: string; savedAt: string };
  /** Message replied to / forwarded. */
  sourceId?: string;
  initial?: ComposerInitialValues;
  /** Initial attachments: the original's files on forward, the mailbox item on forward as attachment. */
  attachments?: readonly DraftAttachment[];
}

/** State shared between the shell regions (folder tree, search, composer) and the mail page. */
export type SearchScope = 'folder' | 'folderTree' | 'mailbox' | 'allMailboxes';

export interface MailContextValue {
  /** The current successful first mail page and essential preferences have committed. */
  onInitialMailRendered: () => void;
  initialPreferencesReady: boolean;
  quarantineFilters: { search: string; zone: string; from: string; to: string };
  setQuarantineFilters: (value: { search: string; zone: string; from: string; to: string }) => void;
  folders: readonly Folder[];
  /** The folders as shown in the tree (localized default names, icons), e.g. for the folder picker. */
  folderItems: readonly FolderTreeItem[];
  reloadFolders: () => void;
  folderId: string;
  search: string;
  /** Last query explicitly submitted from the shell search box. */
  searchQuery: string;
  clearSearchQuery: () => void;
  searchScope: SearchScope;
  setSearchScope: (scope: SearchScope) => void;
  searchFilter: SearchRequest['filter'];
  setSearchFilter: (filter: SearchRequest['filter']) => void;
  searchPanelOpen: boolean;
  setSearchPanelOpen: (open: boolean) => void;
  /** The user's own addresses (login + identities), excluded from reply-all. */
  selfAddresses: readonly string[];
  /** Opens the composer (new mail, reply, reply all, forward); asks first when one with changes is open. */
  compose: (request?: ComposeRequest) => void;
  /**
   * The open composer, placed by the mail page in its reading-pane column (one tree position for all
   * layouts, so switching pane/full width/docked keeps what was typed); undefined when none is open.
   */
  composer: ReactNode;
  /** Layout of the open composer (undefined: none open). */
  composerLayout: ComposerLayout | undefined;
  /**
   * Runs `action` (e.g. open another message) after the reading-pane composer was closed: right
   * away when nothing was typed, otherwise after Save draft / Discard / Cancel.
   */
  leaveComposer: (action: () => void) => void;
  /** Load the signatures again after the user changed them (Settings → Signatures). */
  reloadSignatures: () => void;
  /** Bumped after every send and folder action (mark all read, empty), so the open folder can refresh. */
  listVersion: number;
  mailDrag: { item: DraggedMailItem; sourceFolderIds: string[] } | undefined;
  setMailDrag: Dispatch<SetStateAction<MailContextValue['mailDrag']>>;
  /** Current page owns validation and success-only list updates; no handler off the mail page. */
  mailDropHandlerRef: RefObject<((id: string, target: string, mode: 'move' | 'copy') => void) | undefined>;
}

export const MailContext = createContext<MailContextValue | null>(null);

export function useMail(): MailContextValue {
  const context = useContext(MailContext);
  if (!context) throw new Error('useMail must be used inside the webmail layout.');
  return context;
}
